/* =========================================================
   Services page — cloud (force graph) + "service tree".
   The cloud is the navigator: clicking a node selects it (and
   reshapes the tree below), it never navigates. Only the tree
   cards' Explore / Read more links leave the page.
   Both cloud nodes and tree cards read the same `data-connected`
   graph, so the relationships live in one place.
   ========================================================= */
(function () {
  'use strict';

  /* ---------- graph data from DOM (nodes + connections) ---------- */
  function extractGraphDataFromDOM() {
    const nodes = [], links = [];
    const els = document.querySelectorAll('.word-node');
    els.forEach(el => nodes.push({ id: el.id }));
    els.forEach(el => {
      const from = el.id, conn = el.dataset.connected;
      if (conn) conn.split(',').map(s => 'node-' + s.trim()).forEach(to => {
        if (to && from !== to) links.push({ source: from, target: to });
      });
    });
    return { nodes, links };
  }

  const INITIAL_CHARGE_STRENGTH = -600,
        ACCENTUATED_CHARGE_STRENGTH = -2000,
        SUPER_ACCENTUATED_CHARGE_STRENGTH = 1.5 * ACCENTUATED_CHARGE_STRENGTH,
        HOVER_CHARGE_STRENGTH = -900;

  const graphData = extractGraphDataFromDOM();
  const mediaQuery = window.matchMedia('(max-width: 991.98px)');
  let staticMode = mediaQuery.matches;
  const cloudContainer = document.getElementById('cloud-container');
  const mindEl = document.getElementById('mindmap-container');
  if (staticMode) cloudContainer.classList.add('is-static');

  let boundaries = mindEl.getBoundingClientRect(),
      width = boundaries.width,
      height = boundaries.height;

  const cloudSvg = d3.select('#mindmap-svg');
  const link = cloudSvg.append('g').selectAll('.link')
    .data(graphData.links).enter().append('line').attr('class', 'link');

  const htmlNodes = d3.select(mindEl).selectAll('.word-node');
  const node = htmlNodes
    .data(graphData.nodes, function (d) { return (d || this).id; })
    .on('click', clickHandler)
    .on('mouseover', mouseOverNode)
    .on('mouseout', mouseOutNode);

  graphData.nodes.forEach(n => { n.x = width / 2; n.y = height / 2; });

  let simulation = null;
  let selectedNode = null;

  function ticked() {
    node.style('transform', function (d) {
      const w = this.offsetWidth, h = this.offsetHeight;
      d.x = Math.max(20 + w / 2, Math.min(width - 20 - w / 2, d.x));
      d.y = Math.max(20 + h / 2, Math.min(height - 20 - h / 2, d.y));
      return `translate(${d.x - w / 2}px, ${d.y - h / 2}px)`;
    });
    link
      .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
      .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
  }

  function buildSimulation() {
    if (staticMode) return;
    simulation = d3.forceSimulation(graphData.nodes)
      .force('link', d3.forceLink(graphData.links).id(d => d.id).distance(200))
      .force('charge', d3.forceManyBody().strength(INITIAL_CHARGE_STRENGTH))
      .force('collide', d3.forceCollide(60).iterations(8))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .alphaTarget(0)
      .on('tick', ticked);
  }
  buildSimulation();

  function getConnectedNodeIds(n) {
    return graphData.links
      .filter(l => linkEndsAt(l, n.id))
      .map(l => endId(l.source) === n.id ? endId(l.target) : endId(l.source));
  }
  function endId(e) { return e && e.id ? e.id : e; }
  function linkEndsAt(l, id) { return endId(l.source) === id || endId(l.target) === id; }

  function applyLinkStates(hovered) {
    link
      .classed('link--active', l => (hovered && linkEndsAt(l, hovered.id)) || (selectedNode && linkEndsAt(l, selectedNode.id)))
      .classed('link--dim', l => !!selectedNode && !((hovered && linkEndsAt(l, hovered.id)) || linkEndsAt(l, selectedNode.id)));
  }

  function mouseOverNode(event, n) {
    if (selectedNode === n) return;
    const isNeighborOfSelected = selectedNode && getConnectedNodeIds(selectedNode).includes(n.id);
    d3.selectAll('.word-node').classed('neighbor-hover', false);
    const connected = getConnectedNodeIds(n);
    d3.select('#' + n.id).classed('hover', true);
    connected.forEach(id => d3.select('#' + id).classed('neighbor-hover', true));
    applyLinkStates(n);
    if (staticMode || !simulation) return;
    simulation.force('charge').strength(d => {
      if (selectedNode !== null) {
        const sel = getConnectedNodeIds(selectedNode);
        if (d.id === selectedNode.id) return SUPER_ACCENTUATED_CHARGE_STRENGTH;
        if (sel.includes(d.id) && d.id !== n.id) return ACCENTUATED_CHARGE_STRENGTH;
      }
      return d.id === n.id
        ? (isNeighborOfSelected ? ACCENTUATED_CHARGE_STRENGTH + -400 : HOVER_CHARGE_STRENGTH)
        : INITIAL_CHARGE_STRENGTH;
    });
    simulation.alphaTarget(0.1).restart();
  }

  function mouseOutNode(event, n) {
    if (selectedNode === n) return;
    d3.select('#' + n.id).classed('hover', false);
    d3.selectAll('.word-node').classed('neighbor-hover', false);
    applyLinkStates(null);
    if (staticMode || !simulation) return;
    simulation.force('charge').strength(d => {
      if (selectedNode !== null) {
        const sel = getConnectedNodeIds(selectedNode);
        if (d.id === selectedNode.id) return SUPER_ACCENTUATED_CHARGE_STRENGTH;
        if (sel.includes(d.id)) return ACCENTUATED_CHARGE_STRENGTH;
      }
      return INITIAL_CHARGE_STRENGTH;
    });
    if (selectedNode) simulation.alphaTarget(0.1).restart();
    else simulation.alphaTarget(0);
  }

  function selectNode(t) {
    const d = typeof t === 'string' ? graphData.nodes.find(n => n.id === t) : t;
    if (!d) return;
    const slug = d.id.replace('node-', '');
    try { history.pushState(null, '', '#' + slug); } catch (e) {}
    htmlNodes.classed('selected', false).classed('highlighted', false)
      .classed('hover', false).classed('neighbor-hover', false);
    graphData.nodes.forEach(n => { n.fx = null; n.fy = null; });
    selectedNode = d;
    d3.select('#' + d.id).classed('selected', true);
    getConnectedNodeIds(d).forEach(id => d3.select('#' + id).classed('highlighted', true));
    applyLinkStates(null);
    if (!staticMode && simulation) {
      d.fx = width / 2; d.fy = height / 2;
      simulation.force('charge').strength(n =>
        n.id === d.id ? SUPER_ACCENTUATED_CHARGE_STRENGTH
        : getConnectedNodeIds(d).includes(n.id) ? ACCENTUATED_CHARGE_STRENGTH
        : INITIAL_CHARGE_STRENGTH);
      simulation.alphaTarget(1).restart();
    }
    Tree.render(slug);            /* tree follows the selection */
  }

  function clearSelection() {
    selectedNode = null;
    htmlNodes.classed('selected', false).classed('highlighted', false)
      .classed('hover', false).classed('neighbor-hover', false);
    graphData.nodes.forEach(n => { n.fx = null; n.fy = null; });
    try { history.pushState(null, '', ' '); } catch (e) {}
    applyLinkStates(null);
    if (!staticMode && simulation) {
      simulation.force('charge').strength(INITIAL_CHARGE_STRENGTH);
      simulation.alphaTarget(0).restart();
    }
    Tree.render(Tree.DEFAULT_SLUG);  /* tree back to the default service */
  }

  function clickHandler(event, d) {
    event.preventDefault();         /* select, never navigate away */
    if (selectedNode === d) clearSelection();
    else selectNode(d);
  }

  function fadeInGraph() {
    htmlNodes.transition().duration(1000).style('opacity', 1);
    const svgEl = document.getElementById('mindmap-svg');
    if (svgEl) svgEl.style.opacity = 1;
  }

  /* =========================================================
     SERVICE TREE — BFS rings, organic scatter layout, animated
     reorder. Layout is deterministic (hash-seeded jitter), so it
     reads the same on every load.
     ========================================================= */
  const Tree = (function () {
    const DEFAULT_SLUG = 'moodle-development';
    const stage = document.getElementById('tree-stage');
    const linksSvg = document.getElementById('tree-links');
    const section = document.getElementById('service-tree');
    const cards = new Map();
    document.querySelectorAll('.svc-card').forEach(el => cards.set(el.dataset.slug, el));
    if (!stage || !section || !cards.size) {
      return { render: function () {}, DEFAULT_SLUG: DEFAULT_SLUG };
    }
    section.classList.add('js');

    const ringLabels = Array.from(stage.querySelectorAll('.tree-ring-label'));

    /* adjacency from the same data-connected attributes (undirected, like the cloud) */
    const ADJ = {};
    document.querySelectorAll('.word-node').forEach(el => {
      const slug = el.id.replace('node-', '');
      ADJ[slug] = ADJ[slug] || [];
      (el.dataset.connected || '').split(',').map(s => s.trim()).filter(Boolean).forEach(t => {
        if (!ADJ[slug].includes(t)) ADJ[slug].push(t);
        ADJ[t] = ADJ[t] || [];
        if (!ADJ[t].includes(slug)) ADJ[t].push(slug);
      });
    });

    let positions = new Map();
    let rafId = null;

    /* deterministic pseudo-random per slug, for the organic jitter */
    function hash01(str) {
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return ((h >>> 0) % 10000) / 10000;
    }
    const jx = s => (hash01(s + 'x') - 0.5) * 30;   /* ±15px */
    const jy = s => (hash01(s + 'y') - 0.5) * 22;   /* ±11px */
    const jr = s => (hash01(s + 'r') - 0.5) * 2.4;  /* ±1.2deg */

    function bfs(root) {
      const ringOf = { [root]: 0 }, parent = { [root]: null }, order = [root];
      const queue = [root];
      while (queue.length) {
        const cur = queue.shift();
        for (const nb of ADJ[cur] || []) {
          if (!(nb in ringOf)) {
            ringOf[nb] = ringOf[cur] + 1;
            parent[nb] = cur;
            order.push(nb);
            queue.push(nb);
          }
        }
      }
      /* ring 1 = direct neighbours, everything further = ring 2 */
      for (const s of order) if (s !== root && ringOf[s] > 2) ringOf[s] = 2;
      /* safety: never drop a card, even if the graph changes */
      for (const s of cards.keys()) if (!(s in ringOf)) { ringOf[s] = 2; parent[s] = root; order.push(s); }
      return { ringOf, parent, order };
    }

    function setRingClasses(ringOf, order) {
      for (const slug of cards.keys()) {
        const el = cards.get(slug);
        el.classList.remove('ring-0', 'ring-1', 'ring-2');
        el.classList.add('ring-' + ringOf[slug]);
        const no = el.querySelector('.svc-card__ringno');
        if (no) no.textContent = ringOf[slug] === 0 ? '00' : ringOf[slug] === 1 ? '01' : '02';
        /* entrance stagger (first reveal) */
        el.style.setProperty('--ed', (order.indexOf(slug) * 55) + 'ms');
        /* motion stagger by ring */
        const delay = ringOf[slug] === 0 ? 0
          : ringOf[slug] === 1 ? 90 + order.indexOf(slug) * 45
          : 220 + order.indexOf(slug) * 32;
        el.style.setProperty('--d', delay + 'ms');
      }
    }

    function computeLayout(ringOf, parent, order) {
      const W = stage.clientWidth;
      const pos = new Map();
      const featured = cards.get(order[0]);
      const fh = featured.offsetHeight, fw = featured.offsetWidth;
      const fx = (W - fw) / 2, fy = 10;
      pos.set(order[0], { x: fx, y: fy, w: fw, h: fh, ring: 0, parent: null, rot: 0 });

      const ring1 = order.filter(s => ringOf[s] === 1);
      const ring2 = order.filter(s => ringOf[s] === 2);

      const n1 = ring1.length;
      const s1 = Math.min(318, (W - 24) / Math.max(n1, 1));
      const y1 = fy + fh + 104;
      let ring1Bottom = y1;
      ring1.forEach((slug, i) => {
        const el = cards.get(slug);
        const w = el.offsetWidth, h = el.offsetHeight;
        const x = W / 2 + (i - (n1 - 1) / 2) * s1 - w / 2 + jx(slug);
        const y = y1 + jy(slug) + (i % 2 ? 14 : 0);
        pos.set(slug, { x, y, w, h, ring: 1, parent: parent[slug], rot: jr(slug) });
        ring1Bottom = Math.max(ring1Bottom, y + h);
      });

      const half = Math.ceil(ring2.length / 2);
      const rows = [ring2.slice(0, half), ring2.slice(half)];
      let rowY = ring1Bottom + (n1 ? 96 : 0);
      let bottom = rowY;
      rows.forEach((rowSlugs, ri) => {
        const n = rowSlugs.length;
        if (!n) return;
        const s2 = Math.min(312, (W - 30) / n);
        let rowBottom = rowY;
        rowSlugs.forEach((slug, i) => {
          const el = cards.get(slug);
          const w = el.offsetWidth, h = el.offsetHeight;
          const x = W / 2 + (i - (n - 1) / 2) * s2 - w / 2 + (ri ? s2 * 0.22 : -s2 * 0.1) + jx(slug);
          const y = rowY + jy(slug) + (i % 2 ? 12 : 0);
          pos.set(slug, { x, y, w, h, ring: 2, parent: parent[slug], rot: jr(slug) });
          rowBottom = Math.max(rowBottom, y + h);
        });
        rowY = rowBottom + 42;
        bottom = rowBottom;
      });

      for (const p of pos.values()) p.x = Math.max(4, Math.min(W - p.w - 4, p.x));
      return { pos, bottom, ringTops: { 0: fy, 1: n1 ? y1 : null, 2: ring2.length ? ring1Bottom + (n1 ? 96 : 0) : null } };
    }

    function drawConnectors() {
      const sr = stage.getBoundingClientRect();
      linksSvg.setAttribute('width', stage.clientWidth);
      linksSvg.setAttribute('height', stage.clientHeight);
      let out = '';
      for (const [slug, p] of positions) {
        if (!p.parent) continue;
        const parEl = cards.get(p.parent), el = cards.get(slug);
        if (!parEl || !el) continue;
        const pr = parEl.getBoundingClientRect(), cr = el.getBoundingClientRect();
        const x1 = pr.left - sr.left + pr.width / 2, y1 = pr.top - sr.top + pr.height - 2;
        const x2 = cr.left - sr.left + cr.width / 2, y2 = cr.top - sr.top + 4;
        const my = (y1 + y2) / 2;
        const catRgb = getComputedStyle(el).getPropertyValue('--cat-rgb').trim();
        const cls = p.ring === 1 ? 'is-root' : '';
        out += `<path class="${cls}" style="--cat-rgb:${catRgb}" d="M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}"/>`;
        out += `<circle style="--cat-rgb:${catRgb}" cx="${x2}" cy="${y2}" r="2.4"/>`;
      }
      linksSvg.innerHTML = out;
    }

    function animateConnectors(durationMs) {
      if (rafId) cancelAnimationFrame(rafId);
      const t0 = performance.now();
      const step = now => {
        drawConnectors();
        if (now - t0 < durationMs) rafId = requestAnimationFrame(step);
        else { rafId = null; drawConnectors(); }
      };
      rafId = requestAnimationFrame(step);
    }

    function render(slug, animate = true) {
      if (!cards.has(slug)) return;

      if (staticMode) {
        const { ringOf, order } = bfs(slug);
        setRingClasses(ringOf, order);
        const frag = document.createDocumentFragment();
        order.forEach(s => frag.appendChild(cards.get(s)));
        stage.appendChild(frag);
        return;
      }

      const { ringOf, parent, order } = bfs(slug);
      setRingClasses(ringOf, order);
      void stage.offsetHeight; /* reflow so widths/heights settle */
      const { pos, bottom, ringTops } = computeLayout(ringOf, parent, order);
      positions = pos;

      for (const [s, p] of pos) {
        const el = cards.get(s);
        if (!animate) { el.style.transitionDuration = '0s'; el.style.transitionDelay = '0s'; }
        el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) rotate(${p.rot}deg)`;
        if (!animate) requestAnimationFrame(() => { el.style.transitionDuration = ''; el.style.transitionDelay = ''; });
      }

      stage.style.height = Math.ceil(bottom + 30) + 'px';
      linksSvg.setAttribute('height', Math.ceil(bottom + 30));

      ringLabels.forEach(lbl => {
        const r = +lbl.dataset.ring;
        const top = ringTops[r];
        if (top === null || top === undefined) { lbl.style.opacity = 0; return; }
        lbl.style.opacity = 1;
        lbl.style.transform = `translateY(${r === 0 ? top + 8 : top - 38}px)`;
      });
      const catRgb = getComputedStyle(cards.get(slug)).getPropertyValue('--cat-rgb').trim();
      ringLabels.forEach(lbl => lbl.style.setProperty('--cat-rgb', catRgb));

      const maxDelay = 220 + order.length * 32 + 620;
      animateConnectors(animate ? maxDelay : 60);
    }

    /* cards select the node in the cloud (real links inside still navigate) */
    cards.forEach((el, slug) => {
      el.addEventListener('click', function (e) {
        if (e.target.closest('a')) return;
        const nodeId = 'node-' + slug;
        if (selectedNode && selectedNode.id === nodeId) clearSelection();
        else selectNode(nodeId);
      });
      el.addEventListener('mouseenter', function () {
        const d = graphData.nodes.find(n => n.id === 'node-' + slug);
        if (d) mouseOverNode(null, d);
      });
      el.addEventListener('mouseleave', function () {
        const d = graphData.nodes.find(n => n.id === 'node-' + slug);
        if (d) mouseOutNode(null, d);
      });
    });

    /* entrance reveal */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(entries => {
        entries.forEach(en => {
          if (en.isIntersecting) { section.classList.add('in'); io.disconnect(); }
        });
      }, { threshold: 0.08 });
      io.observe(section);
    } else {
      section.classList.add('in');
    }

    return { render, DEFAULT_SLUG };
  })();

  /* ---------- resize handling ---------- */
  let resizeT = null;
  function onResize() {
    clearTimeout(resizeT);
    resizeT = setTimeout(() => {
      staticMode = mediaQuery.matches;
      cloudContainer.classList.toggle('is-static', staticMode);
      boundaries = mindEl.getBoundingClientRect();
      width = boundaries.width; height = boundaries.height;
      if (simulation) { simulation.stop(); simulation = null; }
      buildSimulation();
      if (selectedNode) {
        selectedNode.fx = width / 2; selectedNode.fy = height / 2;
        if (simulation) simulation.alphaTarget(0.6).restart();
      } else if (simulation) {
        simulation.alphaTarget(0.1).restart();
      }
      Tree.render(selectedNode ? selectedNode.id.replace('node-', '') : Tree.DEFAULT_SLUG, false);
    }, 180);
  }
  window.addEventListener('resize', onResize);
  if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', onResize);

  /* ---------- init ---------- */
  function initialize() {
    let initialId = null;
    if (window.location.hash) {
      const candidate = 'node-' + window.location.hash.substring(1);
      if (graphData.nodes.find(n => n.id === candidate)) initialId = candidate;
    }
    if (initialId) {
      selectNode(initialId);
      setTimeout(fadeInGraph, staticMode ? 150 : 700);
    } else {
      applyLinkStates(null);
      if (!staticMode && simulation) simulation.alphaTarget(0.1).restart();
      setTimeout(fadeInGraph, staticMode ? 150 : 1000);
      Tree.render(Tree.DEFAULT_SLUG, false);
    }
  }
  initialize();
})();
